import { useEffect, useRef, useState } from "react";
import { Alert, Box, Center, Loader } from "@mantine/core";
import type { PDFDocumentProxy } from "pdfjs-dist";

interface LegacyPdfPageProps {
  document: PDFDocumentProxy;
  pageNumber: number;
  label: string;
}

export default function LegacyPdfPage({
  document,
  pageNumber,
  label,
}: LegacyPdfPageProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let renderTask:
      | ReturnType<Awaited<ReturnType<PDFDocumentProxy["getPage"]>>["render"]>
      | undefined;
    const renderPage = async () => {
      try {
        const page = await document.getPage(pageNumber);
        if (cancelled || !canvasRef.current) return;
        const viewport = page.getViewport({ scale: 1.6 });
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");
        if (!context) throw new Error("Canvas unavailable");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        renderTask = page.render({ canvasContext: context, viewport });
        await renderTask.promise;
        if (!cancelled) setLoading(false);
        page.cleanup();
      } catch {
        if (!cancelled) {
          setFailed(true);
          setLoading(false);
        }
      }
    };
    void renderPage();
    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [document, pageNumber]);

  return (
    <Box
      pos="relative"
      mih={200}
      aria-busy={loading}
      role="img"
      aria-label={`${label}, page ${pageNumber}`}
    >
      {loading && (
        <Center mih={200}>
          <Loader aria-label="Loading document page" />
        </Center>
      )}
      {failed && (
        <Alert color="red" title="Page unavailable" role="alert">
          Open the original document or retry the preview.
        </Alert>
      )}
      <canvas
        ref={canvasRef}
        aria-hidden
        style={{
          width: "100%",
          height: "auto",
          display: loading || failed ? "none" : "block",
        }}
      />
    </Box>
  );
}
